import { Grid, Tab, Typography } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import React from 'react';
import { HorizontalActionWindowButtons } from 'src/action-window/components/horizontal-action-window-button-area';
import { IMiddleAreaState } from 'src/common/interfaces/states/IMiddleAreaState';
import { ScannedDataHandler, updateScannerCallback } from 'src/devices/scanner-operations';
import {
  onAddItemDataScanned,
  onRecallOrderDataScanned,
} from 'src/home-page/business-logic/submit-scanned-data';
import TabPanel from 'src/tab-panel/tab-panel';
import { getMiddleAreaTabs } from '../business-logic/middle-area-tabs';
import { styles } from './middle-area-styles';
import { useTranslation } from 'react-i18next';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';

const MiddleArea = ({
  setMiddleAreaState,
  tabValue,
  trainingModeActive,
  activeTransaction,
}: IMiddleAreaProps): JSX.Element => {
  const classes = styles();
  const trainingStyles = trainingModeStyles();
  const tabs = getMiddleAreaTabs(activeTransaction);
  const { t } = useTranslation();

  const handleChange = (newValue: number): void => {
    setMiddleAreaState({ tabValue: newValue });
  };

  return (
    <Grid container direction='column' className={`fullHeight fullWidth`} wrap='nowrap'>
      <Grid item className='fullWidth' style={{ maxHeight: '9.63vh' }}>
        <CustomTabs
          value={tabValue}
          onChange={(unused: any, number: number): void => handleChange(number)}
          className={classes.customTabs}
          spreadOut={true}
          newStyleArrows={true}
          classes={{
            flexContainer: classes.allTabsContainer,
            scrollButtons: classes.scrollButton,
          }}
        >
          {tabs.map(({ tabId, displayName, disabledWhen, isActive }) => {
            let scannerCallbackParam: ScannedDataHandler | undefined = undefined;
            if (tabId === 'action-window') {
              scannerCallbackParam = onAddItemDataScanned;
            }
            if (tabId === 'recall-order') {
              scannerCallbackParam = onRecallOrderDataScanned;
            }
            const activeTransactionDisabled = disabledWhen && disabledWhen(activeTransaction);

            return (
              <Tab
                label={<Typography variant='body2'>{t(displayName || '')}</Typography>}
                key={tabId}
                className={classes.tab}
                onClick={() => updateScannerCallback(scannerCallbackParam)}
                data-testid={`${tabId}-tab-button-test`}
                disabled={!isActive || activeTransactionDisabled}
              />
            );
          })}
        </CustomTabs>
      </Grid>
      <Grid item className={classes.customMaxHeight}>
        {tabs.map(({ tabId, component: Component, buttonList }, index) => {
          return (
            <TabPanel value={tabValue} index={index} key={tabId}>
              <Grid
                container
                direction='column'
                className={`fullHeight ${classes.panel} ${
                  trainingModeActive ? trainingStyles.trainingModeBackground : ''
                }`}
              >
                {buttonList && (
                  <HorizontalActionWindowButtons
                    titleProps={{ title: t(buttonList.title ?? ''), testId: buttonList.testId }}
                    buttonProps={{
                      actionList: buttonList.actions().map(action => ({
                        ...action,
                        text: t(action.text),
                      })),
                    }}
                    trainingModeActive={trainingModeActive}
                  />
                )}
                {!buttonList && Component && <Component />}
              </Grid>
            </TabPanel>
          );
        })}
      </Grid>
    </Grid>
  );
};

export interface IMiddleAreaProps {
  activeTransaction: boolean;
  tabValue: number;
  setMiddleAreaState: (state: Partial<IMiddleAreaState>) => void;
  trainingModeActive: boolean;
}

export default MiddleArea;
